import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom"; // Import navigate
import Select  from "react-select";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const SalesOrderInvoicing = ({ orderRequestCode }) => {
  const [orderRequest, setOrderRequest] = useState(null);
  const [approvedItems, setApprovedItems] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state
  const navigate = useNavigate(); // Initialize navigate
  const [totalAmount, setTotalAmount] = useState(0); // Add totalAmount state
  const [locations, setLocations] = useState([]);
  const [currentUser, setCurrentUser] = useState("");
  const [soInclusiveProducts, setSoInclusiveProducts] = useState([])
  const [locationId, setLocationId] = useState("")
  const [selectedSoInclusiveProducts, setSelectedSoInclusiveProducts] = useState([])

  useEffect(() => {
    const fetchCurrentUser = async () => {
      const token = sessionStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token missing. Please log in.");
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: { Authorization: `Bearer ${token}` },
        })

        const userData = response.data;
        setCurrentUser(userData);

        // Fetch locations and products based on role
        if (userData.role === "Main Admin") {
          fetchAllLocations();
        } else if (userData.role === "Company Admin") {
          fetchFilteredLocations(userData)
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to fetch user data.");
      }

      fetchCurrentUser();
    };

    const fetchAllLocations = async () => {
      const token = sessionStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/locations`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setLocations(response.data.locations)
    }

    const fetchFilteredLocations = async () => {
      const token = sessionStorage.getItem("token")
      const response = await axios.get(`${API_BASE_URL}/products/filtered-locations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLocations(response.data.locations);
    }


    const fetchOrderRequest = async () => {
      if (!orderRequestCode) {
        console.error("Missing orderRequestCode!")
        toast.error("Order Request Code is missing!")
        return;
      }
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          console.error("Authentication token is missing!");
          toast.error("Please log in again.");
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/orders/order-requests/${orderRequestCode}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });


        setOrderRequest(response.data);

        setLocationId(response.data.location_id);

        const items =
          response.data?.orderRequestItems?.map((item) => ({
            ...item,
            approved_qty: item.remaining_qty,
            partial_qty_allocation: item.partial_qty_allocation,
          })) || []
        setApprovedItems(items);


        // Calculate the total amount
        const calculatedTotal = items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0)
        setTotalAmount(calculatedTotal)

        await fetchSOInclusiveProducts(response.data.location?.id)
      } catch (error) {
        console.error("Error fetching order request details:", error);
        toast.error("Failed to fetch order request details.");
      } finally {
        setLoading(false);
      }
    }

    fetchOrderRequest()
  }, [orderRequestCode])

  const fetchSOInclusiveProducts = async (locationId) => {
    try {
      const token = sessionStorage.getItem("token")
      if (!token) {
        console.error("Authentication token is missing!")
        toast.error("Please log in again.")
        return
      }


      const response = await axios.get(`${API_BASE_URL}/latestStock/getSOInclusiveProducts/${locationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setSoInclusiveProducts(response.data.products)
    } catch (error) {
      console.error("Error fetching SO Inclusive products: ", error)
    }
  }

  const handleApprovedQtyChange = (productId, approvedQty) => {
    const numberApprovedQty = Number.parseFloat(approvedQty) || 0

    const updatedItems = approvedItems.map((item) => {
      if (item.product_id === productId) {
        //if product qty is not modifiable and approved qty is greater than remaining qty then refrain from creating sales order
        if (!item.qty_modifiable && numberApprovedQty > item.remaining_qty) {
          toast.error(`Cannot approve more than the remaining quantity of ${item.remaining_qty}`)
          return item;
        }


        return { ...item, approved_qty: numberApprovedQty }
      }
      return item
    })

    setApprovedItems(updatedItems)
    recalculateTotal(updatedItems, selectedSoInclusiveProducts)
  }

  const handleSoInclusiveQtyChange = (productId, approvedQty) => {
    const numberApprovedQty = Number.parseFloat(approvedQty) || 0

    const updatedSoItems = selectedSoInclusiveProducts.map((item) => {
      if (item.product_id === productId) {
        if (numberApprovedQty > item.available_stock) {
          toast.error(`Cannot approve more than the available stock of ${item.available_stock}`)
          return item
        }
        return { ...item, approved_qty: numberApprovedQty }
      }
      return item;
    });

    setSelectedSoInclusiveProducts(updatedSoItems);
    recalculateTotal(approvedItems, updatedSoItems);
  }

  const recalculateTotal = (items, soItems) => {
    const itemsTotal = items.reduce((sum, item) => sum + item.approved_qty * item.unit_price, 0)
    const soItemsTotal = soItems.reduce((sum, item) => sum + item.approved_qty * item.latest_price, 0)
    setTotalAmount(itemsTotal + soItemsTotal)
  }

  const handleAddSoInclusiveProduct = (e) => {
    const selectedProductId = e.target.value
    if (!selectedProductId) return

    const selected = soInclusiveProducts.find((p) => p.product_id === selectedProductId)
    if (selected) {
      const exists = selectedSoInclusiveProducts.find((item) => item.product_id === selected.product_id)
      if (exists) {
        toast.error("Product already added.")
        return
      }
      const newSoItems = [
        ...selectedSoInclusiveProducts,
        {
          ...selected,
          approved_qty: 0,
        },
      ]
      setSelectedSoInclusiveProducts(newSoItems)
      recalculateTotal(approvedItems, newSoItems)
    }
    // Reset dropdown
    e.target.value = ""
  }

  const handleRemoveSoInclusiveProduct = (productId) => {
    const updatedSoItems = selectedSoInclusiveProducts.filter((item) => item.product_id !== productId)
    setSelectedSoInclusiveProducts(updatedSoItems)
    recalculateTotal(approvedItems, updatedSoItems)
  }

  const hasZeroRemainingQty = () => {
    return approvedItems.every((item) => item.remaining_qty === 0);
  }

  //function to validate partially qty allowed products
  const validateApprovedItems = () => {
    for (const item of approvedItems) {
      if (!Boolean(item.partial_qty_allocation) && !Number.isInteger(item.approved_qty)) {
        toast.error(`Quantity for ${item.product_name} must be a whole number.`);
        return false;
      }
    }

    for (const item of selectedSoInclusiveProducts) {
      if (!Boolean(item.partial_qty_allocation) && !Number.isInteger(item.approved_qty)) {
        toast.error(`Quantity for ${item.product_name} must be a whole number.`)
        return false
      }
    }
    return true
  }

  //function to check if all items have 0 approved quantity
  const hasAllZeroApprovedQty = () => {
    const regularApprovedQtySum = approvedItems.reduce((sum, item) => sum + parseFloat(item.approved_qty || 0), 0);
    const soInclusiveApprovedQtySum = selectedSoInclusiveProducts.reduce((sum, item) => sum + parseFloat(item.approved_qty || 0), 0);
    return (regularApprovedQtySum + soInclusiveApprovedQtySum) === 0;
  }

  //function to check if there are any so inclusive products with 0 approved quantity
  const hasZeroApprovedQtyInInclusive = selectedSoInclusiveProducts.some(
    (item) => Number(item.approved_qty) === 0
  );

  //function to add sales order
  const handleConfirmOrder = async () => {
    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing!");
        return;
      }


      //check if the relevant order request is closed
      if (orderRequest.status === "Closed") {
        toast.error("This order request is already closed and cannot be processed.");
        return;
      }

      //called function to check if every items have 0 remaining quantity
      if (hasZeroRemainingQty()) {
        toast.error("Cannot create sales order! All the items have 0 remaining quantity.");
        return;
      }

      if (!validateApprovedItems()) {
        return;
      }

      // Step 1: Fetch the ObjectId of the customer based on customer code
      const customerResponse = await axios.get(`${API_BASE_URL}/customers/get-by-code/sales/${orderRequest.customer.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      const customerId = customerResponse.data.customer._id


      // Step 2: Prepare the sales order data including SO Inclusive products
      const regularItems = approvedItems.map((item) => ({
        product_id: item.product_id,
        requested_qty: item.quantity,
        approved_qty: item.approved_qty,
        prev_rem_qty: item.remaining_qty,
        unit_price: Number.parseFloat(item.unit_price).toFixed(2),
        total_price: Number.parseFloat(item.approved_qty * item.unit_price).toFixed(2),
      }))

      const soInclusiveItems = selectedSoInclusiveProducts.map((item) => ({
        product_id: item.product_id,
        requested_qty: 0, // SO Inclusive products don't have requested qty
        approved_qty: item.approved_qty,
        prev_rem_qty: 0, // SO Inclusive products don't have remaining qty
        unit_price: Number.parseFloat(item.latest_price).toFixed(2),
        total_price: Number.parseFloat(item.approved_qty * item.latest_price).toFixed(2),
      }))

      const allItems = [...regularItems, ...soInclusiveItems]

      const salesOrderData = {
        organization: currentUser.role === "Company Admin" ? currentUser.organization._id : null,
        order_request_id: orderRequest.orderRequestCode,
        location_id: orderRequest.location?.id, // Access location ID
        pickup_location_id: orderRequest.pickup_location?.id,
        customer_id: customerId, // Pass the ObjectId here
        items: allItems,
        total_amount: Number.parseFloat(totalAmount).toFixed(2),
        tax_amount: 0,
        final_amount: Number.parseFloat(totalAmount).toFixed(2),
        address: orderRequest.customer.address || "N/A",
      };


      if (hasAllZeroApprovedQty()) {
        toast.error("Cannot create sales order! All items have 0 approved quantity.");
        return;
    }

      //function call to cvalidate so inclusive product approved quantities
      if (hasZeroApprovedQtyInInclusive) {
        toast.error("Cannot create sales order! SO Inclusive product/s have 0 approved quantity.");
        return;
      }
      // Step 3: Send the sales order data to the backend
      const response = await axios.post(`${API_BASE_URL}/salesOrders/add`, salesOrderData, {
        headers: { Authorization: `Bearer ${token}` },
      });


      if (response.data) {
        await axios.put(
          `${API_BASE_URL}/orders/update-reaming-quantity/${orderRequestCode}`,
          { approvedItems },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        await axios.put(
          `${API_BASE_URL}/orders/check-status/${orderRequestCode}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success("Sales order created successfully!");
        // navigate("/sales-orders"); // Navigate to the sales orders page
      }
    } catch (error) {
      console.error("Error creating sales order:", error);
      toast.error("Failed to create sales order. Please try again.");
    }
  };

  if (loading) {
    return <div>Loading...</div>; // Show loading spinner or message
  }

  if (!orderRequest) {
    return <div>No order request details available.</div>; // Handle missing data
  }

  //component for react select so inclusive product options
  const soOptions = soInclusiveProducts.map(product => ({
    value: product.product_id,
    label: product.product_name,
    stock: product.available_stock,
    image: product.product_image, 
    fullProduct: product,  
  }));

  //function handling product change
  const handleSelectChange = (selectedOption) => {
    const selectedProduct = selectedOption.fullProduct;

    const exists = selectedSoInclusiveProducts.find(item => item.product_id === selectedProduct.product_id);
    if (exists) {
      toast.error("Product already added.");
      return;
    }

    const newSoItems = [
      ...selectedSoInclusiveProducts,
      {
        ...selectedProduct,
        approved_qty: 0,
      },
    ];
    setSelectedSoInclusiveProducts(newSoItems);
    recalculateTotal(approvedItems, newSoItems);
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h1 className="text-2xl font-bold mb-4">Create Sales Order</h1>
      <div className="grid grid-cols-2 gap-6">
        {/* Order Request Details */}
        <div>
          <h2 className="text-lg font-semibold mb-3">Order Details</h2>
          <table className="table-auto w-full bg-white shadow-lg rounded-md">
            <thead className="bg-gray-200">
              <tr>
                <th className="p-2">Product Name</th>
                <th className="p-2">Requested Qty</th>
                <th className="p-2">Remaining Qty</th>
                <th className="p-2">Approved Qty</th>
              </tr>
            </thead>
            <tbody>
              {approvedItems.map((item) => (
                <tr key={item.product_id} className="border-b">
                  <td className="p-2">{item.product_name}</td>
                  <td className="p-2">{item.quantity}</td>
                  <td className="p-2">{item.remaining_qty}</td>
                  <td className="p-2">
                    <input
                      type="number"
                      value={item.approved_qty}
                      onChange={(e) => handleApprovedQtyChange(item.product_id, e.target.value)}
                      className="border border-gray-300 rounded-md p-1 w-20"
                      min="0"
                      max={item.qty_modifiable ? undefined : item.remaining_qty}
                      step={item.partial_qty_allocation ? "0.25" : "1"} //Adjust step dynamically
                      disabled={item.remaining_qty === 0}
                    />
                  </td>
                </tr>
              ))}
              {selectedSoInclusiveProducts.map((item) => (
                <tr key={`so-${item.product_id}`} className="border-b bg-blue-50">
                  <td className="p-2">
                    {item.product_name}
                    <span className="text-xs font-semibold text-blue-600 ml-2">(Additional)</span>
                  </td>
                  <td className="p-2">-</td>
                  <td className="p-2">-</td>
                  <td className="p-2">
                    <input
                      type="number"
                      value={item.approved_qty}
                      onChange={(e) => handleSoInclusiveQtyChange(item.product_id, e.target.value)}
                      className="border border-gray-300 rounded-md p-1 w-20"
                      min="0"
                      max={item.available_stock}
                      step={item.partial_qty_allocation ? "0.25" : "1"}
                    />
                    <button
                      onClick={() => handleRemoveSoInclusiveProduct(item.product_id)}
                      className="ml-2 text-red-500 font-semibold hover:text-red-700 text-xs"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4 mb-4">
            <h3 className="text-md font-semibold mb-2">Add Additional (SO Inclusive) Products</h3>
            <div className="flex gap-4 items-center">
              <Select
                options={soOptions}
                onChange={handleSelectChange}
                placeholder="Select Product"
                formatOptionLabel={(data) => (
                  <div className="flex items-center gap-2">
                    <img
                      src={`${process.env.REACT_APP_IMAGE_BASE_URL}${data.image}`}
                      alt={data.label}
                      className="w-8 h-8 object-cover rounded"
                    />
                    <div>
                      <div>{data.label}</div>
                      <div className="text-xs text-gray-500">Stock: {data.stock}</div>
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
          <button
            onClick={handleConfirmOrder}
            className="bg-green-700 hover:bg-blue-600 text-white px-4 py-2 rounded-md mt-4"
          >
            Confirm Sales Order
          </button>
        </div>

        {/* Invoice Preview */}
        <div id="invoice" className="p-4 bg-white shadow-lg rounded-md">
          <h2 className="text-lg font-semibold mb-3">Invoice Preview</h2>
          <div>
            <p>
              <strong>Order Request Code:</strong> {orderRequest?.orderRequestCode}
            </p>
            <p>
              <strong>Customer Name:</strong> {orderRequest?.customer?.name || "Unknown"}
            </p>
            <p>
              <strong>Date:</strong> {new Date().toLocaleDateString()}
            </p>
          </div>
          <table className="table-auto w-full bg-white mt-4">
            <thead className="bg-gray-200">
              <tr>
                <th className="p-2">Product Name</th>
                <th className="p-2">Approved Qty</th>
                <th className="p-2">Unit Price</th>
                <th className="p-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {approvedItems.map((item) => (
                <tr key={item.product_id} className="border-b">
                  <td className="p-2">{item.product_name}</td>
                  <td className="p-2">{item.approved_qty}</td>
                  <td className="p-2">{Number.parseFloat(item.unit_price).toFixed(2)}</td>
                  <td className="p-2">{Number.parseFloat(item.unit_price * item.approved_qty).toFixed(2)}</td>
                </tr>
              ))}
              {selectedSoInclusiveProducts.map((item) => (
                <tr key={`so-invoice-${item.product_id}`} className="border-b bg-blue-50">
                  <td className="p-2">
                    {item.product_name}
                    <span className="text-xs font-semibold text-blue-600 ml-1">(Additional)</span>
                  </td>
                  <td className="p-2">{item.approved_qty}</td>
                  <td className="p-2">{Number.parseFloat(item.latest_price).toFixed(2)}</td>
                  <td className="p-2">{Number.parseFloat(item.latest_price * item.approved_qty).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-100">
              <tr>
                <td colSpan="3" className="text-right font-semibold p-2">
                  Total Amount:
                </td>
                <td className="p-2 font-semibold">{Number.parseFloat(totalAmount).toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SalesOrderInvoicing;
